import { get, isNull, omitBy } from 'lodash-es';

import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { LEAD_FORM_EVENTS } from 'site-modules/shared/constants/lead-form/lead-form-events';
import { LEAD_TYPES } from 'site-modules/shared/constants/lead-form/lead-form-params';
import { getCreativeId, getInvTreatmentList, getMatchTypeList } from 'client/engagement-handlers/helper';
import { fireListTracking } from 'client/engagement-handlers/list-engagement-handler/list-engagement-handler';
import { getVehiclePrices } from 'client/actions/vehicle';

import { getDealerDistanceList, getInventoryLeadYesNoList, getStyleIdList } from './lead-form-engagement-helper';

/**
 * Fire event on lead form display
 *
 * @param { Object } event { detail: { leadType } , target }
 */
export function fireDisplayLeadFormTracking({ target, detail }) {
  const { leadType, isUserOpened, leadFormConfig } = detail;
  const eventData = {
    action_name: TrackingConstant.ACTION_LEAD_SUBMISSION,
    action_cause: 'page_load',
    action_category: isUserOpened ? TrackingConstant.USER_ACTION_CATEGORY : TrackingConstant.SYSTEM_ACTION_CATEGORY,
    subaction_name: leadFormConfig?.isSimulatedLeadForm
      ? TrackingConstant.DISPLAY_LEAD_FORM_SIMULATED
      : TrackingConstant.DISPLAY_LEAD_FORM,
    inventory_lead: leadType === LEAD_TYPES.INVENTORY,
    creative_id: getCreativeId(target),
  };
  const priceCheckerBestDealPrice = get(leadFormConfig, 'bestDealPrice', 0);

  EventToolbox.fireTrackAction({
    event_type: 'action_start',
    event_data: eventData,
    price_checker_best_deal_price: priceCheckerBestDealPrice,
  });
}

/**
 * Fire event on input validation
 *
 * @param { Object } event { detail: { fieldName, isValid } , target }
 */
export function fireUserInputTracking({ target, detail }) {
  const { fieldName, isValid } = detail;
  let name;

  if (fieldName === 'phone' || fieldName === 'phoneNumber') name = (isValid && 'phone_success') || 'phone_fail';
  else if (fieldName === 'email') name = (isValid && 'email_success') || 'email_fail';
  else return;

  const subactionNames = {
    phone_success: TrackingConstant.PHONE_INPUT_SUCCESS,
    phone_fail: TrackingConstant.PHONE_INPUT_FAIL,
    email_success: TrackingConstant.EMAIL_INPUT_SUCCESS,
    email_fail: TrackingConstant.EMAIL_INPUT_FAIL,
  };
  const eventData = {
    action_name: TrackingConstant.ACTION_LEAD_SUBMISSION,
    action_cause: 'user_input',
    action_category: 'user',
    subaction_name: subactionNames[name],
    creative_id: getCreativeId(target),
  };

  EventToolbox.fireTrackAction({
    event_type: 'action_progress',
    event_data: eventData,
  });
}

/**
 * Fire event on user submit click
 *
 * @param { Object } event { detail: { total, valid, validationErrors } , target }
 */
export function fireUserSubmitTracking({ target, detail }) {
  const { total, valid, validationErrors, isSimulatedLeadForm } = detail;

  const eventData = {
    action_name: TrackingConstant.ACTION_LEAD_SUBMISSION,
    action_cause: 'button_click',
    action_category: 'user',
    subaction_name: isSimulatedLeadForm ? TrackingConstant.ATTEMPT_SUBMIT_SIMULATED : TrackingConstant.ATTEMPT_SUBMIT,
    creative_id: getCreativeId(target),
    error: Object.keys(validationErrors).join().replace(/Name/g, ''),
    total,
    valid,
  };

  EventToolbox.fireTrackAction({
    event_type: 'action_progress',
    event_data: eventData,
  });
}

/**
 * Fire event on success submit
 *
 * @param { Object } event { detail: { isBlended, total, type } , target }
 */
export function fireSystemSubmitTracking({ target, detail }) {
  const { isBlended, total, type, isSimulatedLeadForm } = detail;
  const eventData = {
    action_name: TrackingConstant.ACTION_LEAD_SUBMISSION,
    action_cause: 'button_click',
    action_category: 'system',
    subaction_name: isSimulatedLeadForm
      ? TrackingConstant.PROCESSED_SUBMIT_SIMULATED
      : TrackingConstant.PROCESSED_SUBMIT,
    creative_id: getCreativeId(target),
    isBlended,
    total,
    type,
  };

  if (detail.dealerId) eventData.value = detail.dealerId;

  EventToolbox.fireTrackAction({
    event_type: 'action_completed',
    event_data: eventData,
  });
}

/**
 * Fire list inventories event
 *
 * @param { Object } event { detail: { leadTargets, leadSubmitted, trackingData } , target }
 * @param { Object } extendDetail
 */
export function fireLeadFormListTracking({ target, detail }, extendDetail = {}) {
  const { leadTargets, trackingData } = detail;
  const inventoryLeadYesNoList = getInventoryLeadYesNoList(leadTargets);
  const styleIdList = getStyleIdList(leadTargets);
  const leadIdList = get(trackingData, 'leadIdList', []);
  const radius = get(trackingData, 'radius', null);

  // filter by inventory and remove null values
  const inventories = [];
  leadTargets.forEach(lead => inventories.push(omitBy(lead.inventory || { dealerInfo: { ...lead.dealer } }, isNull)));

  const subactionName = get(trackingData, 'subactionName', '');

  const milesAway = inventories.length ? { miles_away: getDealerDistanceList(leadTargets).join() } : {};

  fireListTracking(inventories, getCreativeId(target), {
    inv_treatment_list: getInvTreatmentList(inventories),
    match_type_list: getMatchTypeList(inventories, radius),
    match_results: inventories.length,
    match_total: inventories.length,
    inventory_lead_yes_no: inventoryLeadYesNoList.join(),
    style_id_list: styleIdList.join(),
    lead_uuid_list: leadIdList.join(),
    ...extendDetail,
    ...milesAway,
    ...(subactionName ? { subaction_name: subactionName } : {}),
  });
}

/**
 * Fire list inventories event
 *
 * @param { Object } event
 */
export function fireLeadFormSelectStyleTracking(event) {
  fireLeadFormListTracking(event, {
    action_name: 'inventory_select',
    subaction_name: 'style_select',
    action_cause: 'user_input',
    action_category: 'user',
  });
}

/**
 * Fire de-select / select lead cards
 *
 * @param { Object } event
 */
export function fireLeadFormSelectCardTracking({ target, detail }) {
  const { checked, slot } = detail;
  const eventData = {
    action_name: TrackingConstant.ACTION_LEAD_SUBMISSION,
    action_cause: 'user_input',
    action_category: 'user',
    subaction_name: checked ? 'check_card' : 'uncheck_card',
    creative_id: getCreativeId(target),
    value: slot,
  };

  EventToolbox.fireTrackAction({
    event_type: 'action_progress',
    event_data: eventData,
  });
}

/**
 * Fire end lead submission event
 *
 * @param { Object } event { detail: { trackingData } , target }
 */
export function fireEndLeadSubmissionTracking({ detail: { leadTargets, isSimulatedLeadForm }, target }) {
  const eventData = {
    action_name: TrackingConstant.ACTION_LEAD_SUBMISSION,
    action_cause: 'button_click',
    action_category: 'user',
    subaction_name: isSimulatedLeadForm
      ? TrackingConstant.DISPLAY_THANK_YOU_SIMULATED
      : TrackingConstant.DISPLAY_THANK_YOU,
    creative_id: getCreativeId(target),
  };
  leadTargets.forEach((leadTarget, index) => {
    const vin = get(leadTarget, 'inventory.vin');
    const { price, salePrice } = getVehiclePrices(get(leadTarget, 'inventory.prices'));
    const eventType = index === 0 ? 'action_end' : 'action_end_multi';
    const trim = get(leadTarget, 'inventory.vehicleInfo.styleInfo.trim');

    EventToolbox.fireTrackAction({
      event_type: eventType,
      event_data: eventData,
      lead_data: {
        vin,
        price,
        trim,
        salePrice,
      },
    });
  });
}

export const LeadFormEngagementHandler = {
  init: () => {
    EventToolbox.on(LEAD_FORM_EVENTS.DISPLAY, fireDisplayLeadFormTracking);
    EventToolbox.on(LEAD_FORM_EVENTS.LIST, fireLeadFormListTracking);
    EventToolbox.on(LEAD_FORM_EVENTS.INPUT, fireUserInputTracking);
    EventToolbox.on(LEAD_FORM_EVENTS.SELECT_STYLE, fireLeadFormSelectStyleTracking);
    EventToolbox.on(LEAD_FORM_EVENTS.SELECT_CARD, fireLeadFormSelectCardTracking);
    EventToolbox.on(LEAD_FORM_EVENTS.ATTEMPT_SUBMIT, fireUserSubmitTracking);
    EventToolbox.on(LEAD_FORM_EVENTS.SUBMIT, fireSystemSubmitTracking);
    EventToolbox.on(LEAD_FORM_EVENTS.THANK_YOU, fireEndLeadSubmissionTracking);
  },
};
